(() => {
  'use strict';
  if (window.__uniProgramV5Ready) return;
  window.__uniProgramV5Ready = true;
  const instances = new Map();
  const mobile = window.matchMedia('(max-width: 999px)');
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');

  function init(root) {
    if (instances.has(root)) return;
    const nav = root.querySelector('[data-program-nav]');
    const index = root.querySelector('[data-program-index]');
    if (!nav || !index) return;
    const summary = index.querySelector('summary');
    const current = index.querySelector('[data-program-index-current]');
    const links = [...nav.querySelectorAll('a[href^="#"]')];
    const entries = links.flatMap(link => {
      try {
        const target = document.getElementById(decodeURIComponent(link.hash.slice(1)));
        return target ? [{ link, target }] : [];
      } catch (_) { return []; }
    });
    const header = document.querySelector('[data-refined-header], [data-header]');
    const headerFrame = header?.querySelector('.uni-header__frame') || header;
    const abort = new AbortController();
    const listen = { signal: abort.signal };
    let frame = null;
    let active = null;
    let headerHeight = 0;
    let indexHeight = 0;
    const motionAllowed = () => !reduced.matches && document.body.dataset.motion !== 'none' && document.body.dataset.motionUser !== 'none' && !root.closest('[data-motion="none"]') && (!window.UniMotion?.allowed || window.UniMotion.allowed(root));
    const compactIndex = () => root.dataset.distanceExperience === 'true' && root.dataset.selectedModality === 'distancia';
    const offset = () => headerHeight + (mobile.matches && !compactIndex() ? indexHeight : 0) + 24;
    const setActive = entry => {
      if (active === entry) return;
      active = entry;
      links.forEach(link => link.removeAttribute('aria-current'));
      entry?.link.setAttribute('aria-current', 'location');
      if (current) current.textContent = entry ? entry.link.textContent.trim() : '';
    };
    const update = () => {
      frame = null;
      let next = null;
      const readingLine = offset() + 8;
      for (const entry of entries) {
        if (!entry.target.hidden && entry.target.getBoundingClientRect().top <= readingLine) next = entry;
      }
      setActive(next);
    };
    const schedule = () => { if (frame === null) frame = window.requestAnimationFrame(update); };
    const measure = () => {
      const headerSection = header?.closest('.shopify-section') || header;
      const position = headerSection ? window.getComputedStyle(headerSection).position : '';
      headerHeight = (position === 'sticky' || position === 'fixed') && !headerSection.classList.contains('is-scroll-hidden') ? Math.ceil(headerFrame.getBoundingClientRect().height) : 0;
      indexHeight = mobile.matches && !compactIndex() ? Math.ceil(summary.getBoundingClientRect().height) + 2 : 0;
      root.style.setProperty('--uni-program-header-offset', `${headerHeight}px`);
      root.style.setProperty('--uni-program-index-height', `${indexHeight}px`);
      schedule();
    };
    const syncMode = () => {
      index.open = !compactIndex() && !mobile.matches;
      measure();
    };
    const focusTarget = target => {
      const hadTabindex = target.hasAttribute('tabindex');
      if (!hadTabindex) {
        target.setAttribute('tabindex', '-1');
        target.addEventListener('blur', () => target.removeAttribute('tabindex'), { once: true });
      }
      target.focus({ preventScroll: true });
    };
    const visit = (event, entry) => {
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      event.preventDefault();
      if (mobile.matches || compactIndex()) index.open = false;
      measure();
      const url = new URL(window.location.href);
      url.hash = entry.target.id;
      if (url.href !== window.location.href) window.history.pushState(window.history.state, '', url);
      focusTarget(entry.target);
      window.scrollTo({ top: Math.max(0, window.scrollY + entry.target.getBoundingClientRect().top - offset()), behavior: motionAllowed() ? 'smooth' : 'instant' });
      setActive(entry);
    };
    entries.forEach(entry => entry.link.addEventListener('click', event => visit(event, entry), listen));
    index.addEventListener('toggle', measure, listen);
    index.addEventListener('keydown', event => {
      if (event.key === 'Escape' && (mobile.matches || compactIndex()) && index.open) {
        event.preventDefault();
        index.open = false;
        summary.focus({ preventScroll: true });
      }
    }, listen);
    window.addEventListener('scroll', schedule, { ...listen, passive: true });
    window.addEventListener('resize', measure, { ...listen, passive: true });
    window.addEventListener('hashchange', schedule, listen);
    window.addEventListener('pageshow', measure, listen);
    document.addEventListener('uni:headerchange', measure, listen);
    mobile.addEventListener('change', syncMode, listen);
    document.addEventListener('uni:modalitychange', event => {
      if (event.detail?.id === root.dataset.programId) syncMode();
    }, listen);
    const resize = typeof ResizeObserver === 'function' ? new ResizeObserver(measure) : null;
    if (headerFrame) resize?.observe(headerFrame);
    if (summary) resize?.observe(summary);
    syncMode();
    instances.set(root, () => {
      abort.abort();
      resize?.disconnect();
      if (frame !== null) window.cancelAnimationFrame(frame);
      links.forEach(link => link.removeAttribute('aria-current'));
      root.style.removeProperty('--uni-program-header-offset');
      root.style.removeProperty('--uni-program-index-height');
    });
  }

  const initWithin = scope => {
    if (scope instanceof Element && scope.matches('uni-program')) init(scope);
    scope.querySelectorAll('uni-program').forEach(init);
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initWithin(document), { once: true });
  else initWithin(document);
  document.addEventListener('shopify:section:load', event => initWithin(event.target));
  document.addEventListener('shopify:section:unload', event => {
    for (const [root, dispose] of instances) {
      if (event.target === root || event.target.contains(root)) { dispose(); instances.delete(root); }
    }
  });
})();

